import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  ExpandLessOutlined as ExpandLessIcon,
  ExpandMoreOutlined as ExpandMoreIcon,
} from '@mui/icons-material';
import { getErrorMessage } from '@/utils/errors';
import { skillsPluginsApi } from '@/services/api';
import type { CatalogEntryRequestCreate, CatalogEntryRequest } from '@/services/api';

interface SkillsPluginsTableProps {
  teamId: string;
  canEdit: boolean;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

type TypeFilter = 'all' | 'skill' | 'plugin';

const EMPTY_REQUEST_FORM: CatalogEntryRequestCreate = {
  name: '',
  entry_type: 'skill',
  marketplace_repo: '',
  marketplace_name: '',
  description: '',
  source_url: '',
};

function requestStatusColor(
  status: CatalogEntryRequest['status'],
): 'warning' | 'success' | 'error' {
  if (status === 'pending') return 'warning';
  if (status === 'approved') return 'success';
  return 'error';
}

function requestStatusLabel(status: CatalogEntryRequest['status']): string {
  if (status === 'pending') return 'Pending';
  if (status === 'approved') return 'Approved';
  return 'Rejected';
}

export default function SkillsPluginsTable({ teamId, canEdit, onNotify }: SkillsPluginsTableProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestForm, setRequestForm] = useState<CatalogEntryRequestCreate>(EMPTY_REQUEST_FORM);
  const [requestsExpanded, setRequestsExpanded] = useState<boolean | null>(null);

  const { data: entries, isLoading } = useQuery({
    queryKey: ['team-skills-plugins', teamId],
    queryFn: () => skillsPluginsApi.getTeamCatalog(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: teamRequests } = useQuery({
    queryKey: ['team-sp-requests', teamId],
    queryFn: () => skillsPluginsApi.getTeamRequests(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ entryId, enabled }: { entryId: string; enabled: boolean }) =>
      skillsPluginsApi.setInstallation(teamId, entryId, enabled),
    onSuccess: (_data, { enabled }) => {
      queryClient.invalidateQueries({ queryKey: ['team-skills-plugins', teamId] });
      onNotify(enabled ? 'Installed successfully!' : 'Uninstalled successfully!', 'success');
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ['team-skills-plugins', teamId] });
      onNotify(`Failed to update installation: ${getErrorMessage(error)}`, 'error');
    },
  });

  const submitRequestMutation = useMutation({
    mutationFn: (data: CatalogEntryRequestCreate) =>
      skillsPluginsApi.submitRequest(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-sp-requests', teamId] });
      onNotify('Request submitted successfully!', 'success');
      handleRequestClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to submit request: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleRequestClose = () => {
    setRequestDialogOpen(false);
    setRequestForm(EMPTY_REQUEST_FORM);
  };

  const filtered = (entries ?? []).filter((entry) => {
    if (typeFilter !== 'all' && entry.entry_type !== typeFilter) return false;
    if (search && !entry.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const hasPendingRequests = (teamRequests ?? []).some((r) => r.status === 'pending');
  const pendingCount = (teamRequests ?? []).filter((r) => r.status === 'pending').length;

  // Default: expanded if there are pending requests; collapsed otherwise
  const isExpanded =
    requestsExpanded !== null ? requestsExpanded : hasPendingRequests;

  const showRequestsSection = (teamRequests ?? []).length > 0;

  return (
    <Box>
      {/* My Requests section */}
      {showRequestsSection && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              py: 0.5,
            }}
            onClick={() => setRequestsExpanded(!isExpanded)}
            role="button"
            aria-expanded={isExpanded}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setRequestsExpanded(!isExpanded);
              }
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              My Requests
            </Typography>
            {pendingCount > 0 && (
              <Chip
                label={`${pendingCount} pending`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            <IconButton size="small" tabIndex={-1} aria-hidden="true">
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={isExpanded} unmountOnExit>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Review Note</TableCell>
                    <TableCell>Submitted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(teamRequests ?? []).map((req) => (
                    <TableRow key={req.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {req.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {req.marketplace_repo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.entry_type === 'plugin' ? 'Plugin' : 'Skill'}
                          size="small"
                          color={req.entry_type === 'plugin' ? 'secondary' : 'primary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={requestStatusLabel(req.status)}
                          size="small"
                          color={requestStatusColor(req.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {req.review_note ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(req.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>
      )}

      {/* Catalog header controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search skills & plugins..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <ToggleButtonGroup
          size="small"
          value={typeFilter}
          exclusive
          onChange={(_, value) => value && setTypeFilter(value)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="skill">Skills</ToggleButton>
          <ToggleButton value="plugin">Plugins</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="Request a new skill or plugin to be added to the catalogue">
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setRequestDialogOpen(true)}
            >
              Request New
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Catalog table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Enabled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {entry.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={entry.entry_type === 'plugin' ? 'Plugin' : 'Skill'}
                    size="small"
                    color={entry.entry_type === 'plugin' ? 'secondary' : 'primary'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {entry.source_url ? (
                    <MuiLink href={entry.source_url} target="_blank" rel="noopener noreferrer">
                      {entry.marketplace_repo}
                    </MuiLink>
                  ) : (
                    entry.marketplace_repo
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {entry.description}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Switch
                    aria-label={`Toggle ${entry.name}`}
                    checked={entry.enabled}
                    disabled={!canEdit || toggleMutation.isPending}
                    onChange={(e) =>
                      toggleMutation.mutate({ entryId: entry.id, enabled: e.target.checked })
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No skills or plugins match your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Request New dialog */}
      <Dialog open={requestDialogOpen} onClose={handleRequestClose} maxWidth="sm" fullWidth>
        <DialogTitle>Request New Skill / Plugin</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={requestForm.name}
            onChange={(e) => setRequestForm({ ...requestForm, name: e.target.value })}
            fullWidth
            required
          />
          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={requestForm.entry_type}
              onChange={(e) =>
                setRequestForm({ ...requestForm, entry_type: e.target.value as 'skill' | 'plugin' })
              }
            >
              <MenuItem value="skill">Skill</MenuItem>
              <MenuItem value="plugin">Plugin</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Marketplace Repo"
            value={requestForm.marketplace_repo}
            onChange={(e) => setRequestForm({ ...requestForm, marketplace_repo: e.target.value })}
            placeholder="owner/repo"
            helperText="Format: owner/repo"
            fullWidth
            required
          />
          <TextField
            label="Marketplace Name"
            value={requestForm.marketplace_name}
            onChange={(e) =>
              setRequestForm({ ...requestForm, marketplace_name: e.target.value })
            }
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={requestForm.description}
            onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            label="Source URL"
            value={requestForm.source_url}
            onChange={(e) => setRequestForm({ ...requestForm, source_url: e.target.value })}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRequestClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => submitRequestMutation.mutate(requestForm)}
            disabled={
              !requestForm.name ||
              !requestForm.marketplace_repo ||
              !requestForm.marketplace_name ||
              submitRequestMutation.isPending
            }
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
